import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Tag, Typography, Progress, Button, Empty, Skeleton, Space } from 'antd';
import {
  UserOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  ArrowRightOutlined,
  RiseOutlined,
  ReloadOutlined,
  FileTextOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { userService } from '../services/userService';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { inventoryService } from '../services/inventoryService';
import type { User } from '../types';
import type { PurchaseOrder } from '../types/purchaseOrder';
import { getCurrentUser } from '../utils/rbac';
import { useResponsive } from '../hooks/useResponsive';
import './AdminDashboard.css';

const { Title, Text } = Typography;

type AdminOverviewResponse = {
  daily_sales?: {
    date?: string;
    total_sales?: number;
    total_transactions?: number;
    cash?: number;
    card?: number;
    bank_transfer?: number;
  };
  inventory?: {
    total_products?: number;
    low_stock_count?: number;
    out_of_stock_count?: number;
  };
  purchase_orders?: {
    total?: number;
    sent?: number;
    draft?: number;
  };
};

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  usersGrowth: number;
  totalLocations: number;
  activeLocations: number;
  locationsGrowth: number;
  privilegeRequests: number;
  totalPOs: number;
  totalProducts: number;
  lowStockCount: number;
  outOfStockCount: number;
}

interface UserDistribution {
  role: string;
  count: number;
}

interface RecentUser {
  id: number;
  name: string;
  employeeId: string;
  position: string;
  location: string;
  status: string;
  lastActive: string;
  avatar?: string;
}

interface PurchaseOrderReport {
  poNumber: string;
  manager: string;
  location: string;
  items: number;
  totalAmount: number;
  date: string;
  status: string;
}

interface InventoryReport {
  category: string;
  totalItems: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
  status: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminOverview, setAdminOverview] = useState<AdminOverviewResponse | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    usersGrowth: 0,
    totalLocations: 0,
    activeLocations: 0,
    locationsGrowth: 0,
    privilegeRequests: 0,
    totalPOs: 0,
    totalProducts: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [userDistribution, setUserDistribution] = useState<UserDistribution[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderReport[]>([]);
  const [inventoryReport, setInventoryReport] = useState<InventoryReport[]>([]);

  useEffect(() => {
    loadDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const currentUser = getCurrentUser();
      const currentAdminId = currentUser?.id != null ? Number(currentUser.id) : NaN;
      const isCurrentUser = (u: User) => !Number.isNaN(currentAdminId) && Number(u.id) === currentAdminId;

      const [users, locations, overview, pos, inventoryResponse] = await Promise.all([
        userService.getUsers({ limit: 100 }),
        userService.getLocations({ limit: 100 }),
        apiService.get<AdminOverviewResponse>('/dashboard/admin/overview').catch(() => null),
        purchaseOrderService.getPurchaseOrders({ limit: 5 }).catch(() => [] as PurchaseOrder[]),
        inventoryService.getDetailedStock({ limit: 50 }).catch(() => ({ items: [] as any[] } as any)),
      ]);

      // Exclude current admin from all user stats and lists
      const usersExcludingSelf = users.filter((u: User) => !isCurrentUser(u));

      const activeUsers = usersExcludingSelf.filter((u: User) => u.is_active);
      const currentMonth = new Date().getMonth();
      const newUsersThisMonth = usersExcludingSelf.filter((u: User) => {
        if (!u.created_at) return false;
        const createdDate = new Date(u.created_at);
        return createdDate.getMonth() === currentMonth;
      }).length;

      const activeLocations = locations.filter((l: any) => l.is_active);

      // Calculate user distribution by role (excluding current admin)
      const roleCounts: { [key: string]: number } = {};
      usersExcludingSelf.forEach((u: User) => {
        const roleName = u.role?.name || 'Unknown';
        roleCounts[roleName] = (roleCounts[roleName] || 0) + 1;
      });
      const distribution = Object.entries(roleCounts).map(([role, count]) => ({
        role,
        count,
      }));

      // Get recent users (last 5 by updated_at), excluding current admin
      const recentUsersData = [...usersExcludingSelf]
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 5)
        .map((u: User) => ({
          id: u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
          employeeId: `#EMP${String(u.id).padStart(3, '0')}`,
          position: u.role?.name || 'Unknown',
          location: u.location?.name || 'N/A',
          status: u.is_active ? 'Active' : 'Inactive',
          // We don't currently type `last_login` in `frontend/src/types/index.ts`.
          // Use `updated_at` as a reasonable “last active” proxy.
          lastActive: formatRelativeTime(u.updated_at),
          avatar: getInitials(`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email),
        }));

      const poReports: PurchaseOrderReport[] = pos.map((po: PurchaseOrder) => ({
        poNumber: po.po_number ? `#${po.po_number}` : `#PO-${po.id}`,
        manager: po.created_by_user?.first_name
          ? `${po.created_by_user.first_name} ${po.created_by_user.last_name || ''}`
          : 'N/A',
        location: po.location?.name || 'N/A',
        items: po.items?.length || 0,
        totalAmount: po.total_amount || 0,
        date: po.order_date
          ? new Date(po.order_date).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })
          : 'N/A',
        status: po.status || 'Draft',
      }));

      // Load inventory report (simplified - would need category grouping)
      const inventory = inventoryResponse.items || [];
      // Group by category (simplified - assuming products have categories)
      const categoryData: { [key: string]: InventoryReport } = {};
      inventory.forEach((inv: any) => {
        const category = inv.product?.category || 'Uncategorized';
        if (!categoryData[category]) {
          categoryData[category] = {
            category,
            totalItems: 0,
            inStock: 0,
            lowStock: 0,
            outOfStock: 0,
            status: 'Good',
          };
        }
        categoryData[category].totalItems += 1;
        const available = inv.quantities?.available || 0;
        if (available > 0) {
          categoryData[category].inStock += 1;
        } else {
          categoryData[category].outOfStock += 1;
        }
        if (inv.stock_status === 'Low Stock' && available > 0) {
          categoryData[category].lowStock += 1;
        }
      });
      const inventoryData = Object.values(categoryData).map((cat) => ({
        ...cat,
        status: cat.outOfStock > 0 || cat.lowStock > cat.totalItems * 0.2 ? 'Warning' : 'Good',
      }));

      if (overview) {
        setAdminOverview(overview);
      }

      const totalPOs = overview?.purchase_orders?.total ?? pos.length;
      const invTotal = overview?.inventory?.total_products ?? inventory.length;
      const lowStock = overview?.inventory?.low_stock_count ?? inventory.filter((i: any) => i.stock_status === 'Low Stock').length;
      const outOfStock = overview?.inventory?.out_of_stock_count ?? inventory.filter((i: any) => (i.quantities?.available || 0) === 0).length;

      setStats({
        totalUsers: usersExcludingSelf.length,
        activeUsers: activeUsers.length,
        newUsersThisMonth,
        usersGrowth: 10.7,
        totalLocations: locations.length,
        activeLocations: activeLocations.length,
        locationsGrowth: 2,
        privilegeRequests: 5,
        totalPOs,
        totalProducts: invTotal,
        lowStockCount: lowStock,
        outOfStockCount: outOfStock,
      });
      setUserDistribution(distribution);
      setRecentUsers(recentUsersData);
      setPurchaseOrders(poReports.slice(0, 3));
      setInventoryReport(inventoryData.slice(0, 4));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeTime = (iso: string | undefined | null) => {
    if (!iso) return 'Never';
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 'Just now';
    const minutes = Math.floor(ms / (1000 * 60));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    return `${days} days ago`;
  };

  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (index: number): string => {
    const colors = ['#1890ff', '#52c41a', '#722ed1', '#fa8c16'];
    return colors[index % colors.length];
  };

  const getStatusColor = (status: string): string => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'active':
      case 'good':
      case 'received':
        return 'green';
      case 'pending':
      case 'sent':
        return 'orange';
      case 'warning':
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  const poStatusLabel = (raw: string) => {
    const s = (raw || '').toUpperCase();
    if (s === 'DRAFT') return 'Draft';
    if (s === 'SENT') return 'Pending';
    if (s === 'RECEIVED') return 'Approved';
    if (s === 'CANCELLED') return 'Cancelled';
    return raw || 'Draft';
  };

  // Recent Users columns
  const recentUsersColumns = [
    {
      title: 'EMPLOYEE',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: RecentUser) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: getAvatarColor(record.id),
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
            }}
          >
            {record.avatar}
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{text}</div>
            <div style={{ fontSize: '14px', color: '#666' }}>{record.employeeId}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'POSITION',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'LAST ACTIVE',
      dataIndex: 'lastActive',
      key: 'lastActive',
    },
  ];

  // Purchase Order columns
  const poColumns = [
    {
      title: 'PO NUMBER',
      dataIndex: 'poNumber',
      key: 'poNumber',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'MANAGER',
      dataIndex: 'manager',
      key: 'manager',
    },
    {
      title: 'LOCATION',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'ITEMS',
      dataIndex: 'items',
      key: 'items',
      render: (items: number) => `${items} items`,
    },
    {
      title: 'TOTAL AMOUNT',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `Nu. ${amount.toLocaleString()}`,
    },
    {
      title: 'DATE',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
  ];

  // Inventory Report columns
  const inventoryColumns = [
    {
      title: 'CATEGORY',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'TOTAL ITEMS',
      dataIndex: 'totalItems',
      key: 'totalItems',
    },
    {
      title: 'IN STOCK',
      dataIndex: 'inStock',
      key: 'inStock',
    },
    {
      title: 'LOW STOCK',
      dataIndex: 'lowStock',
      key: 'lowStock',
    },
    {
      title: 'OUT OF STOCK',
      dataIndex: 'outOfStock',
      key: 'outOfStock',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
  ];

  return (
    <div className="admin-dashboard-page" style={{ padding: isMobile ? '12px' : '16px' }}>
      {/* Page header */}
      <div className="admin-dashboard-page-header">
        <div className="admin-dashboard-page-title-block">
          <Title level={3} style={{ margin: 0, fontSize: isMobile ? 20 : 24, color: '#1F2937' }}>
            Admin Dashboard
          </Title>
          <Text className="admin-dashboard-subtitle" style={{ color: '#6B7280' }}>
            Overview of users, locations, orders and inventory
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadDashboardData} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      {error && (
        <Card style={{ marginBottom: 16, borderRadius: 12 }}>
          <Text type="danger">{error}</Text>
        </Card>
      )}

      {/* Overview Cards – 3 on first row, 2 centered on second row for balanced layout */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} className="ad-cards-row">
        <Col xs={24} sm={12} lg={8} xl={8}>
          <Card hoverable className="ad-stat-card ad-stat-card--lavender" onClick={() => navigate('/admin-portal/users')}>
            <div className="ad-stat-card__head">
              <span className="ad-stat-card__title">USER MANAGEMENT</span>
              <div className="ad-stat-card__iconWrap" aria-hidden="true">
                <UserOutlined />
              </div>
            </div>
            <div className="ad-stat-card__value">{stats.totalUsers}</div>
            <div className="ad-stat-card__meta">{stats.newUsersThisMonth} new users this month</div>
            <div className="ad-stat-card__bottom">
              <span className="ad-stat-card__trend">
                <RiseOutlined /> +{stats.usersGrowth}%
              </span>
              <div className="ad-stat-card__bar" style={{ width: '60px', height: '6px', borderRadius: 3, background: 'rgba(102, 126, 234, 0.4)' }} />
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={8}>
          <Card hoverable className="ad-stat-card ad-stat-card--green" onClick={() => navigate('/admin-portal/locations')}>
            <div className="ad-stat-card__head">
              <span className="ad-stat-card__title">LOCATIONS</span>
              <div className="ad-stat-card__iconWrap" aria-hidden="true">
                <EnvironmentOutlined />
              </div>
            </div>
            <div className="ad-stat-card__value">{stats.totalLocations}</div>
            <div className="ad-stat-card__meta">All locations active</div>
            <div className="ad-stat-card__bottom">
              <span className="ad-stat-card__trend">+{stats.locationsGrowth}</span>
              <Tag color="green" className="ad-stat-card__tag">ACTIVE</Tag>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={8}>
          <Card hoverable className="ad-stat-card ad-stat-card--blue" onClick={() => navigate('/admin-portal/purchase-orders')}>
            <div className="ad-stat-card__head">
              <span className="ad-stat-card__title">PURCHASE ORDERS</span>
              <div className="ad-stat-card__iconWrap" aria-hidden="true">
                <FileTextOutlined />
              </div>
            </div>
            <div className="ad-stat-card__value">{stats.totalPOs}</div>
            <div className="ad-stat-card__meta">POs in system</div>
            <div className="ad-stat-card__bottom">
              <Button type="text" size="small" className="ad-stat-card__action" onClick={(e) => { e.stopPropagation(); navigate('/admin-portal/purchase-orders'); }}>
                View
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={{ span: 8, offset: 4 }} xl={{ span: 8, offset: 4 }}>
          <Card hoverable className="ad-stat-card ad-stat-card--orange" onClick={() => navigate('/admin-portal/reports/sales')}>
            <div className="ad-stat-card__head">
              <span className="ad-stat-card__title">SALE REPORT</span>
              <div className="ad-stat-card__iconWrap" aria-hidden="true">
                <BarChartOutlined />
              </div>
            </div>
            <div className="ad-stat-card__value">Nu. {(adminOverview?.daily_sales?.total_sales || 0).toLocaleString()}</div>
            <div className="ad-stat-card__meta">Today&apos;s sales</div>
            <div className="ad-stat-card__bottom">
              <Button type="text" size="small" className="ad-stat-card__action ad-stat-card__action--review" onClick={(e) => { e.stopPropagation(); navigate('/admin-portal/reports/sales'); }}>
                REVIEW
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8} xl={8}>
          <Card hoverable className="ad-stat-card ad-stat-card--teal" onClick={() => navigate('/admin-portal/inventory')}>
            <div className="ad-stat-card__head">
              <span className="ad-stat-card__title">INVENTORY</span>
              <div className="ad-stat-card__iconWrap" aria-hidden="true">
                <InboxOutlined />
              </div>
            </div>
            <div className="ad-stat-card__value">{stats.totalProducts}</div>
            <div className="ad-stat-card__meta">
              {stats.lowStockCount > 0 || stats.outOfStockCount > 0
                ? `${stats.lowStockCount} low stock, ${stats.outOfStockCount} out of stock`
                : 'Stock levels OK'}
            </div>
            <div className="ad-stat-card__bottom">
              {(stats.lowStockCount > 0 || stats.outOfStockCount > 0) && (
                <span className="ad-stat-card__warn">Action Required</span>
              )}
              <Button type="text" size="small" className="ad-stat-card__action" onClick={(e) => { e.stopPropagation(); navigate('/admin-portal/inventory'); }}>
                View
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Left Column */}
        <Col xs={24} lg={16}>
          {/* Sales Report Overview */}
          <Card
            title={<Title level={4} className="admin-dashboard-card-title">Sales Report Overview</Title>}
            extra={
              <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/admin-portal/reports/sales')}>
                View report
              </Button>
            }
            style={{
              marginBottom: '16px',
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>View-only access to sales performance</Text>
            <div style={{ marginTop: 12 }}>
              {loading ? (
                <Skeleton active paragraph={{ rows: 2 }} />
              ) : (
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Text type="secondary">Today Sales</Text>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>
                        Nu. {(adminOverview?.daily_sales?.total_sales || 0).toLocaleString()}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Text type="secondary">Transactions</Text>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>
                        {(adminOverview?.daily_sales?.total_transactions || 0).toLocaleString()}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Card size="small" style={{ borderRadius: 10 }}>
                      <Text type="secondary">Payments mix</Text>
                      <div style={{ marginTop: 8 }}>
                        {(() => {
                          const cash = adminOverview?.daily_sales?.cash || 0;
                          const card = adminOverview?.daily_sales?.card || 0;
                          const bank = adminOverview?.daily_sales?.bank_transfer || 0;
                          const total = cash + card + bank || 1;
                          return (
                            <>
                              <Text style={{ fontSize: 14 }}>Cash</Text>
                              <Progress percent={Math.round((cash / total) * 100)} size="small" showInfo={false} />
                              <Text style={{ fontSize: 14 }}>Card</Text>
                              <Progress percent={Math.round((card / total) * 100)} size="small" showInfo={false} strokeColor="#52c41a" />
                              <Text style={{ fontSize: 14 }}>Bank</Text>
                              <Progress percent={Math.round((bank / total) * 100)} size="small" showInfo={false} strokeColor="#722ed1" />
                            </>
                          );
                        })()}
                      </div>
                    </Card>
                  </Col>
                </Row>
              )}
            </div>
          </Card>

          {/* User Distribution */}
          <Card
            title={<Title level={4} className="admin-dashboard-card-title">User Distribution</Title>}
            extra={<Text type="secondary" style={{ fontSize: 14 }}>Active users by role</Text>}
            style={{
              marginBottom: '16px',
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : userDistribution.length === 0 ? (
              <Empty description="No user distribution data" />
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {userDistribution
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6)
                  .map((dist) => {
                    const pct = Math.round((dist.count / Math.max(1, stats.totalUsers || 1)) * 100);
                    return (
                      <div
                        key={dist.role}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '8px 10px',
                          borderRadius: 10,
                          background: '#fafafa',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate('/admin-portal/users')}
                      >
                        <div style={{ minWidth: 140, fontWeight: 600 }}>{dist.role}</div>
                        <div style={{ flex: 1 }}>
                          <Progress percent={pct} showInfo={false} />
                        </div>
                        <Tag style={{ margin: 0 }}>{dist.count}</Tag>
                      </div>
                    );
                  })}
              </div>
            )}
          </Card>

        </Col>

        {/* Right Column */}
        <Col xs={24} lg={8}>
          {/* Purchase Order Report */}
          <Card
            title={<Title level={4} className="admin-dashboard-card-title">Purchase Orders</Title>}
            extra={
              <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/admin-portal/purchase-orders')}>
                Full report
              </Button>
            }
            style={{
              marginBottom: '16px',
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              View-only summary (top 3)
            </Text>
            <div style={{ marginTop: 12 }}>
              {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : purchaseOrders.length === 0 ? (
                <Empty description="No purchase orders" />
              ) : (
                <Table
                  columns={poColumns.map((c: any) => {
                    if (c.dataIndex === 'status') {
                      return {
                        ...c,
                        render: (status: string) => (
                          <Tag color={getStatusColor(poStatusLabel(status))}>{poStatusLabel(status)}</Tag>
                        ),
                      };
                    }
                    return c;
                  })}
                  dataSource={purchaseOrders}
                  rowKey="poNumber"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                  onRow={(record) => ({
                    onClick: () => navigate('/admin-portal/purchase-orders'),
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </div>
          </Card>

          {/* Inventory Report */}
          <Card
            title={<Title level={4} className="admin-dashboard-card-title">Inventory</Title>}
            extra={
              <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/admin-portal/inventory')}>
                Full report
              </Button>
            }
            style={{
              borderRadius: '12px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Text type="secondary" style={{ fontSize: 14 }}>
              Category status (top 4)
            </Text>
            <div style={{ marginTop: 12 }}>
              {loading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : inventoryReport.length === 0 ? (
                <Empty description="No inventory data" />
              ) : (
                <Table
                  columns={inventoryColumns}
                  dataSource={inventoryReport}
                  rowKey="category"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                  onRow={() => ({
                    onClick: () => navigate('/admin-portal/inventory'),
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Users (full width) */}
      <Card
        title={<Title level={4} className="admin-dashboard-card-title">Recent Users</Title>}
        extra={
          <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/admin-portal/users')}>
            View all
          </Button>
        }
        style={{
          marginTop: 16,
          borderRadius: 12,
          border: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : recentUsers.length === 0 ? (
          <Empty description="No recent users" />
        ) : (
          <Table
            columns={recentUsersColumns}
            dataSource={recentUsers}
            rowKey="id"
            pagination={false}
            size={isMobile ? 'small' : 'middle'}
            scroll={isMobile ? { x: 600 } : undefined}
            onRow={() => ({
              onClick: () => navigate('/admin-portal/users'),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>
    </div>
  );
};

export default AdminDashboard;
